import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart3, Plus, Trash2, ChevronUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

type TriggerType =
  | 'LEAD_CREATED'
  | 'STAGE_CHANGED'
  | 'LEAD_ASSIGNED'
  | 'FOLLOW_UP_DUE'
  | 'DEAL_WON'
  | 'DEAL_LOST';

type ActionType = 'SEND_MESSAGE' | 'SEND_TEMPLATE' | 'ADD_TAG' | 'UPDATE_STAGE';

interface AutomationRule {
  id: string;
  name: string;
  trigger: TriggerType;
  conditions: unknown[];
  action: ActionType;
  actionData: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  executionCount?: number;
}

interface CreateRulePayload {
  name: string;
  trigger: TriggerType;
  conditions: unknown[];
  action: ActionType;
  actionData: Record<string, unknown>;
  isActive: boolean;
}

async function fetchRules(): Promise<AutomationRule[]> {
  const res = await fetch(`${API_BASE}/whatsapp/automation-rules?limit=50&skip=0`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch automation rules');
  const json = await res.json();
  return json.data as AutomationRule[];
}

async function createRule(payload: CreateRulePayload): Promise<AutomationRule> {
  const res = await fetch(`${API_BASE}/whatsapp/automation-rules`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || 'Failed to create rule');
  }
  const json = await res.json();
  return json.data as AutomationRule;
}

async function toggleRule(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/whatsapp/automation-rules/${id}/toggle`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || 'Failed to toggle rule');
  }
}

async function deleteRule(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/whatsapp/automation-rules/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { message?: string }).message || 'Failed to delete rule');
  }
}

const TRIGGER_LABELS: Record<TriggerType, string> = {
  LEAD_CREATED: 'Lead Created',
  STAGE_CHANGED: 'Stage Changed',
  LEAD_ASSIGNED: 'Lead Assigned',
  FOLLOW_UP_DUE: 'Follow-up Due',
  DEAL_WON: 'Deal Won',
  DEAL_LOST: 'Deal Lost',
};

const TRIGGER_BADGE_CLASSES: Record<TriggerType, string> = {
  LEAD_CREATED: 'bg-blue-100 text-blue-800',
  STAGE_CHANGED: 'bg-purple-100 text-purple-800',
  LEAD_ASSIGNED: 'bg-indigo-100 text-indigo-800',
  FOLLOW_UP_DUE: 'bg-orange-100 text-orange-800',
  DEAL_WON: 'bg-green-100 text-green-800',
  DEAL_LOST: 'bg-red-100 text-red-800',
};

const ACTION_LABELS: Record<ActionType, string> = {
  SEND_MESSAGE: 'Send Message',
  SEND_TEMPLATE: 'Send Template',
  ADD_TAG: 'Add Tag',
  UPDATE_STAGE: 'Update Stage',
};

interface FormState {
  name: string;
  trigger: TriggerType;
  action: ActionType;
  message: string;
  isActive: boolean;
}

const DEFAULT_FORM: FormState = {
  name: '',
  trigger: 'LEAD_CREATED',
  action: 'SEND_MESSAGE',
  message: '',
  isActive: true,
};

export const WhatsAppAutomation: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formErrors, setFormErrors] = useState<{ name?: string; message?: string }>({});

  const { data: rules, isLoading, error } = useQuery<AutomationRule[], Error>({
    queryKey: ['whatsapp-automation-rules'],
    queryFn: fetchRules,
  });

  const createMutation = useMutation<AutomationRule, Error, CreateRulePayload>({
    mutationFn: createRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-automation-rules'] });
      setForm(DEFAULT_FORM);
      setShowForm(false);
      toast({ title: 'Rule created', description: 'Automation rule is now active.' });
    },
    onError: (err) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const toggleMutation = useMutation<void, Error, string>({
    mutationFn: toggleRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-automation-rules'] });
    },
    onError: (err) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: deleteRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-automation-rules'] });
      toast({ title: 'Rule deleted', description: 'Automation rule has been removed.' });
    },
    onError: (err) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  function handleFormChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errors: { name?: string; message?: string } = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (form.action === 'SEND_MESSAGE' && !form.message.trim()) {
      errors.message = 'Message is required for Send Message action';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const actionData: Record<string, unknown> = {};
    if (form.action === 'SEND_MESSAGE') actionData.message = form.message.trim();

    createMutation.mutate({
      name: form.name.trim(),
      trigger: form.trigger,
      conditions: [],
      action: form.action,
      actionData,
      isActive: form.isActive,
    });
  }

  function handleDelete(id: string) {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this automation rule?')) return;
    deleteMutation.mutate(id);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Automation Rules</span>
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setShowForm((prev) => !prev)}
            aria-expanded={showForm}
          >
            {showForm ? (
              <>
                <ChevronUp className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                New Rule
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Inline create form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4"
          >
            <h3 className="font-semibold text-gray-900">New Automation Rule</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule Name <span className="text-red-500">*</span>
              </label>
              <Input
                name="name"
                value={form.name}
                onChange={handleFormChange}
                placeholder="e.g. Welcome message for new leads"
              />
              {formErrors.name && (
                <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trigger</label>
                <select
                  name="trigger"
                  value={form.trigger}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {(Object.keys(TRIGGER_LABELS) as TriggerType[]).map((key) => (
                    <option key={key} value={key}>
                      {TRIGGER_LABELS[key]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <select
                  name="action"
                  value={form.action}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {(Object.keys(ACTION_LABELS) as ActionType[]).map((key) => (
                    <option key={key} value={key}>
                      {ACTION_LABELS[key]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {form.action === 'SEND_MESSAGE' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleFormChange}
                  rows={3}
                  placeholder="Message to send automatically..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {formErrors.message && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={form.isActive}
                  onChange={handleFormChange}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
              </label>
              <span className="text-sm text-gray-700">Enable rule immediately</span>
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={createMutation.isPending} size="sm">
                {createMutation.isPending ? 'Creating...' : 'Create Rule'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowForm(false);
                  setForm(DEFAULT_FORM);
                  setFormErrors({});
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Rules list */}
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-10 text-red-500">
            <p>Failed to load automation rules. Please try again.</p>
          </div>
        ) : rules && rules.length > 0 ? (
          <div className="space-y-3">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center gap-4 border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition-colors"
              >
                {/* Active toggle */}
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={rule.isActive}
                    onChange={() => toggleMutation.mutate(rule.id)}
                    disabled={toggleMutation.isPending}
                    className="sr-only peer"
                    aria-label={`Toggle ${rule.name}`}
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
                </label>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-900 text-sm">{rule.name}</p>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TRIGGER_BADGE_CLASSES[rule.trigger]}`}
                    >
                      {TRIGGER_LABELS[rule.trigger] || rule.trigger}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Action: {ACTION_LABELS[rule.action] || rule.action}
                    {rule.executionCount != null && (
                      <span className="ml-3 text-gray-400">
                        {rule.executionCount} execution{rule.executionCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                </div>

                {/* Status indicator */}
                <span
                  className={`text-xs font-medium flex-shrink-0 ${rule.isActive ? 'text-green-600' : 'text-gray-400'}`}
                >
                  {rule.isActive ? 'Active' : 'Inactive'}
                </span>

                {/* Delete */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(rule.id)}
                  disabled={deleteMutation.isPending}
                  title="Delete rule"
                  className="flex-shrink-0"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No automation rules yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Set up rules to send messages automatically based on lead events.
            </p>
            <Button className="mt-4" size="sm" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
